import * as React from 'react';
import * as Icons from '../../icons';
import { ComponentSize } from '../select/__commons__/selectCommons';
import './icon-loader.css';

export type IconName = keyof typeof Icons; // Derive valid icon names dynamically
export type IconSize = Extract<ComponentSize, 'sm' | 'md' | 'lg'>;

const sizeClasses: Record<IconSize, string> = {
    sm: 'w-4 h-4', // 16px
    md: 'w-6 h-6', // 24px (default)
    lg: 'w-8 h-8', // 32px
};

export interface IconProps extends React.SVGProps<SVGSVGElement> {
    iconName: IconName; // Enforce only valid icon names
    size?: IconSize;
    className?: string;
}
/** An icon with an action renders as a button, so it needs `actionLabel` to name it. */
export type IconWithAction = IconProps & { testId?: string } & (
        | { action?: undefined; actionLabel?: undefined }
        | { action: () => void; actionLabel: string }
    );

export const IconLoader: React.FC<IconProps> = ({ iconName, size = 'md', className = '', ...props }) => {
    const SelectedIcon = Icons[iconName]; // Dynamically retrieve the icon component

    if (!SelectedIcon) {
        console.error(`Icon "${iconName}" not found.`);
        return null; // Fallback for invalid icons
    }

    // default fill color class can be overwritten by passing in a different fill class
    const hasFillClass = /^(fill-[\w-]+)|\s(fill-[\w-]+)/.test(className);
    const defaultClass = hasFillClass ? '' : 'fill-system-on-surface-variant';
    // Decorative by default: an icon only belongs in the accessibility tree once the caller names it
    // or marks it as an image. Both attributes sit before the spread so explicit props still win.
    const isNamed =
        Boolean(props['aria-label'] ?? props['aria-labelledby'] ?? (props as { title?: string }).title) ||
        props.role === 'img';
    return (
        <SelectedIcon
            className={`${sizeClasses[size]} ${defaultClass} ${className} iconPathFix`}
            viewBox="0 0 24 24"
            aria-hidden={isNamed ? undefined : true}
            focusable="false"
            {...props}
        />
    );
};
